import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import styled from '@emotion/styled';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { NetworkStatus, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { datadogLogs } from '@datadog/browser-logs';

import ResetPassword from './Scenes/ResetPassword';
import PaymentMethods from './Components/PaymentMethods';
import Payments from './Components/Payments';
import StaffIdleTimeout from '~Components/StaffIdleTimeout';
import * as actions from './actions';
import { isAuthToken } from '~Components/Authentication/AuthService';
import PrintQueue from '~Components/PrintQueue';
import PayAtTable from '~Components/PayAtTable/PayAtTable';
import Toasts from '~Components/Toasts';
import PaymentHandler from './Scenes/Tables/Components/PaymentHandler';
import TokenRefresher from '~Components/Authentication/TokenRefresher';
import { LogManager } from '~Components/Initialization/LogManager';
import { OrderStatusManager } from '~Components/OrderStatusManager';
import { getLogger, useDeviceInfo, useDispatch, useLicenses, useSelector } from '~Hooks';
import DemoHandler from '~Components/DemoHandler';
import MenuFetchingComponent from '~Components/MenuFetchingComponent';
import Loader from '~ComponentsImproved/Loader';
import PrinterManager from '~Components/PrinterManager';
import { useIncomingAppOrders } from '~Providers/IncomingAppOrdersProvider';
import IncomingOrdersModal from '~Scenes/IncomingAppOrders/IncomingOrdersModal';
import IntercomComponent from '~Components/Intercom';
import { getUser } from '~__queries__';
import { Box } from '~Styles';
import Login from '~Scenes/Login';
import NetworkError from '~Components/NetworkError';
import Kivra from '~Components/Kivra';

const LoadingWrapper = styled(Box)`
  height: 100vh;
  background-color: var(--colorBlueGray1200);
`;

const USER_QUERY = gql`
  query getUser {
    user {
      id
      name
      email
      shouldUpdatePassword
      company {
        id
        name
        hasPoints
        hasPOS
        hasTables
        hasAppOrders
        hasKDS
        hasCDS
        hasExpress
        hasInventory
        manualEndOfDay
        useNewLicenses
        isPaymentsOnly
      }
      image
    }
  }
`;

const App: React.FC = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const licenses = useLicenses();
  const deviceInfo = useDeviceInfo();

  const userDataQuery = useQuery<getUser>(USER_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // so `loading` also flips true during refetch(), not just the initial fetch
  });

  const incomingAppOrders = useIncomingAppOrders();

  const [error, setError] = useState<Error | null>(null);
  const logger = getLogger('native-messages');

  const { user, authToken, lastVisitedPath, offline, payments } = useSelector((state) => ({
    user: state.user,
    authToken: state.authToken,
    lastVisitedPath: state.lastVisitedPath,
    offline: state.offline,
    payments: state.POSPayments,
  }));

  const hasValidAuthToken = isAuthToken(authToken);
  const isStaging = window.location.hash.includes('staging');
  const isSelfService = Boolean(
    window.location.hash.includes('self-service-kiosks') ||
      window.location.hash.includes('express'),
  );

  const [hasMountedNetworkErrorPage, setHasMountedNetworkErrorPage] = useState(false);
  useEffect(() => {
    if (userDataQuery.data?.user) {
      return;
    }

    if (userDataQuery.loading || userDataQuery.networkStatus === NetworkStatus.refetch) {
      return;
    }

    if (!hasValidAuthToken) {
      return;
    }

    setHasMountedNetworkErrorPage(true);
  }, [
    userDataQuery.data?.user,
    userDataQuery.loading,
    userDataQuery.networkStatus,
    hasValidAuthToken,
  ]);

  useEffect(() => {
    document.addEventListener('message', onMessage);
    window.addEventListener('message', onMessage);
    window.postMessageReact('loaded');

    if (lastVisitedPath && !__DEV__) {
      history.replace(lastVisitedPath);
    }

    const unlisten = history.listen((location, action) => {
      if (action === 'PUSH') {
        dispatch({ type: actions.SET_LAST_VISITED_PATH, path: location.pathname });
      }
    });

    if (!window.hasNativeWrapper && window.__DEV__) {
      const ws = new WebSocket('ws://localhost:8080');
      ws.onmessage = (event) => {
        window.postMessage(event.data, '*');
      };
    }

    return () => {
      document.removeEventListener('message', onMessage);
      window.removeEventListener('message', onMessage);
      unlisten();
    };
  }, [history, lastVisitedPath, dispatch]);

  useEffect(() => {
    if (!userDataQuery.data?.user) {
      return;
    }

    Sentry.setUser({
      email: userDataQuery.data.user.email,
      id: userDataQuery.data.user.id,
      username: userDataQuery.data.user.name,
    });

    const company = userDataQuery.data.user.company;
    if (company) {
      dispatch({ type: actions.SET_COMPANY, companyId: company.id });

      dispatch({
        type: 'SET_COMPANY_FLAGS',
        flags: {
          hasPoints: company.hasPoints,
          hasPOS: company.hasPOS,
          hasTables: company.hasTables,
          hasAppOrders: company.hasAppOrders,
          hasKDS: company.hasKDS,
          hasCDS: company.hasCDS,
          hasExpress: company.hasExpress,
          hasInventory: company.hasInventory,
          manualEndOfDay: company.manualEndOfDay,
          useNewLicenses: company.useNewLicenses,
          isPaymentsOnly: company.isPaymentsOnly,
        },
      });

      datadogLogs.setGlobalContextProperty('company', company.name);
    } else {
      datadogLogs.removeGlobalContextProperty('company');
    }
  }, [userDataQuery.data, dispatch]);

  const onMessage = (message: any) => {
    let data = message.data;
    try {
      data = JSON.parse(message.data);
    } catch (e) {
      // Do nothing
    }

    if (data.type) {
      logNativeMessages(data);
      dispatch({ ...data, type: `MESSAGE_${data.type}` });
    }
  };

  const logNativeMessages = (data: any) => {
    const actionsToLog = [
      'PAYMENT_BRIDGE_REQUEST_SUCCESS',
      'PAYMENT_BRIDGE_REQUEST_FAILED',
      'REFUND_BRIDGE_REQUEST_SUCCESS',
      'REFUND_BRIDGE_REQUEST_FAILED',
      'DEVICE_SLEEP',
      'DEVICE_WAKE',
    ];

    if (actionsToLog.includes(data.type)) {
      logger.info(`Message from native wrapper (${data.type})`, {
        messageData: data,
      });
    }
  };

  // This is to prevent the "POS" button to render after all the other buttons in Handheld homescreen
  const isLicensesLoadingOnHandheld = licenses.loading && deviceInfo?.isHandheldTerminal;

  if ((userDataQuery.loading || isLicensesLoadingOnHandheld) && !hasMountedNetworkErrorPage) {
    return (
      <LoadingWrapper display="flex" justifyContent="center" alignItems="center">
        <Loader />
      </LoadingWrapper>
    );
  }

  if (!hasValidAuthToken) {
    return <Login refetch={async () => await userDataQuery.refetch()} />;
  }

  // Valid token but no user data — likely a network error, not a logout
  if (!userDataQuery.data?.user && !userDataQuery.loading) {
    return <NetworkError refetch={async () => await userDataQuery.refetch()} />;
  }

  if (userDataQuery.data?.user?.shouldUpdatePassword) {
    return <ResetPassword onSuccess={() => userDataQuery.refetch()} />;
  }

  return (
    <div>
      <IntercomComponent user={userDataQuery.data?.user} />
      <DemoHandler />
      <LogManager />
      <PrinterManager />
      {!error && children}
      {userDataQuery.data?.user && <TokenRefresher />}
      {userDataQuery.data?.user && <PaymentMethods />}
      {userDataQuery.data?.user && <MenuFetchingComponent />}
      {userDataQuery.data?.user && <Payments />}
      {userDataQuery.data?.user && <StaffIdleTimeout />}
      {userDataQuery.data?.user && <PrintQueue />}
      {userDataQuery.data?.user && <PayAtTable />}
      {userDataQuery.data?.user && <Kivra />}
      {userDataQuery.data?.user?.company.hasKDS && <OrderStatusManager />}
      {userDataQuery.data?.user &&
        payments.map((payment) => <PaymentHandler key={payment.id} payment={payment} />)}
      {userDataQuery.data?.user && !isSelfService && <Toasts />}
      {location.pathname === '/' && userDataQuery.data?.user && <Redirect to="/venues" />}
      {incomingAppOrders.showingIncomingModal && (
        <IncomingOrdersModal hide={incomingAppOrders.hideModal} />
      )}

      {isStaging && (
        <div
          style={{
            position: 'absolute',
            left: '80px',
            top: '80px',
            pointerEvents: 'none',
          }}>
          <h1
            style={{
              fontSize: '160px',
              color: 'red',
              opacity: 0.3,
              lineHeight: '0px',
            }}>
            STAGING
          </h1>
        </div>
      )}
    </div>
  );
};

export default App;
